import { usePermissions } from './usePermissions';
import { ModulePermission, PermissionAction } from '../types/permissions';

/**
 * Hook for trading-specific permission checks
 * Provides convenient methods for checking trading-related permissions
 */
export const useTradingPermissions = () => {
    const { hasPermission, hasAnyPermission, hasAllPermissions, isAdmin } = usePermissions();

    // Order management permissions
    const canViewOrders = () => hasPermission(ModulePermission.ORDERS, PermissionAction.VIEW);
    const canCreateOrders = () => hasPermission(ModulePermission.ORDERS, PermissionAction.CREATE);
    const canEditOrders = () => hasPermission(ModulePermission.ORDERS, PermissionAction.EDIT);
    const canDeleteOrders = () => hasPermission(ModulePermission.ORDERS, PermissionAction.DELETE);
    const canExportOrders = () => hasPermission(ModulePermission.ORDERS, PermissionAction.EXPORT);

    // Trade management permissions
    const canViewTrades = () => hasPermission(ModulePermission.TRADES, PermissionAction.VIEW);
    const canCreateTrades = () => hasPermission(ModulePermission.TRADES, PermissionAction.CREATE);
    const canEditTrades = () => hasPermission(ModulePermission.TRADES, PermissionAction.EDIT);
    const canDeleteTrades = () => hasPermission(ModulePermission.TRADES, PermissionAction.DELETE);
    const canExportTrades = () => hasPermission(ModulePermission.TRADES, PermissionAction.EXPORT);

    // Trade history management permissions
    const canViewTradeHistory = () => hasPermission(ModulePermission.TRADES, PermissionAction.VIEW);
    const canEditTradeHistory = () => hasPermission(ModulePermission.TRADES, PermissionAction.EDIT);
    const canDeleteTradeHistory = () =>
        hasPermission(ModulePermission.TRADES, PermissionAction.DELETE);
    const canExportTradeHistory = () =>
        hasPermission(ModulePermission.TRADES, PermissionAction.EXPORT);
    const canReopenTradeHistory = () =>
        hasPermission(ModulePermission.ORDERS, PermissionAction.CREATE); // Reopening creates new positions

    // Position management permissions
    const canViewPositions = () => hasPermission(ModulePermission.TRADING, PermissionAction.VIEW);
    const canCreatePositions = () =>
        hasPermission(ModulePermission.ORDERS, PermissionAction.CREATE);
    const canEditPositions = () => hasPermission(ModulePermission.TRADING, PermissionAction.EDIT);
    const canClosePositions = () => hasPermission(ModulePermission.TRADING, PermissionAction.EDIT);

    // Customer management permissions
    const canViewCustomers = () => hasPermission(ModulePermission.CUSTOMERS, PermissionAction.VIEW);
    const canEditCustomers = () => hasPermission(ModulePermission.CUSTOMERS, PermissionAction.EDIT);

    // Trading account permissions
    const canViewTradingAccounts = () =>
        hasPermission(ModulePermission.TRADING_ACCOUNTS, PermissionAction.VIEW);
    const canEditTradingAccounts = () =>
        hasPermission(ModulePermission.TRADING_ACCOUNTS, PermissionAction.EDIT);

    // Transaction permissions
    const canViewTransactions = () =>
        hasPermission(ModulePermission.TRANSACTIONS, PermissionAction.VIEW);
    const canEditTransactions = () =>
        hasPermission(ModulePermission.TRANSACTIONS, PermissionAction.EDIT);

    // Bulk operation permissions
    const canPerformBulkOperations = () => {
        return hasAnyPermission([
            { module: ModulePermission.ORDERS, action: PermissionAction.EDIT },
            { module: ModulePermission.TRADES, action: PermissionAction.EDIT },
            { module: ModulePermission.TRADING, action: PermissionAction.EDIT },
        ]);
    };

    // Export permissions
    const canExportTradingData = () => {
        return hasAnyPermission([
            { module: ModulePermission.ORDERS, action: PermissionAction.EXPORT },
            { module: ModulePermission.TRADES, action: PermissionAction.EXPORT },
            { module: ModulePermission.TRADING, action: PermissionAction.EXPORT },
        ]);
    };

    // Analytics permissions
    const canViewTradingAnalytics = () => {
        return hasAnyPermission([
            { module: ModulePermission.ORDERS, action: PermissionAction.VIEW },
            { module: ModulePermission.TRADES, action: PermissionAction.VIEW },
            { module: ModulePermission.TRADING, action: PermissionAction.VIEW },
        ]);
    };

    // Full trading management permissions (for admin-like operations)
    const canManageTrading = () => {
        return hasAllPermissions([
            { module: ModulePermission.ORDERS, action: PermissionAction.EDIT },
            { module: ModulePermission.TRADES, action: PermissionAction.EDIT },
            { module: ModulePermission.TRADING, action: PermissionAction.EDIT },
        ]);
    };

    // Permission groups for UI components
    const orderPermissions = {
        view: canViewOrders(),
        create: canCreateOrders(),
        edit: canEditOrders(),
        delete: canDeleteOrders(),
        export: canExportOrders(),
    };

    const tradePermissions = {
        view: canViewTrades(),
        create: canCreateTrades(),
        edit: canEditTrades(),
        delete: canDeleteTrades(),
        export: canExportTrades(),
    };

    const tradeHistoryPermissions = {
        view: canViewTradeHistory(),
        edit: canEditTradeHistory(),
        delete: canDeleteTradeHistory(),
        export: canExportTradeHistory(),
        reopen: canReopenTradeHistory(),
    };

    const positionPermissions = {
        view: canViewPositions(),
        create: canCreatePositions(),
        edit: canEditPositions(),
        close: canClosePositions(),
    };

    const customerPermissions = {
        view: canViewCustomers(),
        edit: canEditCustomers(),
    };

    const tradingAccountPermissions = {
        view: canViewTradingAccounts(),
        edit: canEditTradingAccounts(),
    };

    const transactionPermissions = {
        view: canViewTransactions(),
        edit: canEditTransactions(),
    };

    return {
        // Individual permission checks
        canViewOrders,
        canCreateOrders,
        canEditOrders,
        canDeleteOrders,
        canExportOrders,
        canViewTrades,
        canCreateTrades,
        canEditTrades,
        canDeleteTrades,
        canExportTrades,
        canViewTradeHistory,
        canEditTradeHistory,
        canDeleteTradeHistory,
        canExportTradeHistory,
        canReopenTradeHistory,
        canViewPositions,
        canCreatePositions,
        canEditPositions,
        canClosePositions,
        canViewCustomers,
        canEditCustomers,
        canViewTradingAccounts,
        canEditTradingAccounts,
        canViewTransactions,
        canEditTransactions,

        // Combined permission checks
        canPerformBulkOperations,
        canExportTradingData,
        canViewTradingAnalytics,
        canManageTrading,

        // Permission groups for easy use in components
        orderPermissions,
        tradePermissions,
        tradeHistoryPermissions,
        positionPermissions,
        customerPermissions,
        tradingAccountPermissions,
        transactionPermissions,

        // Admin check
        isAdmin,
    };
};
